import React, { createContext, useState, useContext, ReactNode, useCallback } from "react";
import { McNotification } from "@maersk-global/mds-react-wrapper/components-core/mc-notification";
import { McIcon } from "@maersk-global/mds-react-wrapper/components-core/mc-icon";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { createPortal } from "react-dom";
import styles from "./Notification.module.scss";
import { handleDownload } from "../../utils/tools";

// Define notification types
export type NotificationType = "success" | "warning" | "error" | "info";

// Define notification item interface
export interface NotificationItem {
  id: string;
  type: NotificationType;
  message: string;
  title?: string;
  duration?: number;
  icon?: string;
  actions?: any;
}

// Define context interface
interface NotificationContextType {
  notifications: NotificationItem[];
  addNotification: (notification: Omit<NotificationItem, "id">) => string;
  removeNotification: (id: string) => void;
}

// Create context
const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Notification provider component
export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [downloading, setDownloading] = useState(false);

  // Remove notification
  const removeNotification = useCallback((id: string): any => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== id));
  }, []);

  // Add notification
  const addNotification = useCallback(
    (notification: Omit<NotificationItem, "id">) => {
      const id = Math.random().toString(36).substring(2, 9);
      const newNotification = {
        ...notification,
        id,
      };

      // Repeated validation attempts should refresh the existing message rather
      // than cover the page with identical notifications.
      setNotifications((prev) => [
        ...prev.filter(
          (existing) =>
            existing.type !== newNotification.type ||
            existing.title !== newNotification.title ||
            existing.message !== newNotification.message
        ),
        newNotification,
      ]);

      // Only set auto-close if duration is explicitly provided
      if (notification.duration) {
        setTimeout(() => {
          removeNotification(id);
        }, notification.duration);
      }

      return id;
    },
    [removeNotification]
  );

  // Render notification components
  const renderNotifications = () => {
    return createPortal(
      <div className={styles.notificationContainer}>
        {notifications.map((notification) => {
          const icon = getNotificationIcon(notification);

          return (
            <div key={notification.id} className={styles.notificationItem}>
              <McNotification
                className={styles.notification}
                width="380px"
                heading={notification.title || getDefaultTitle(notification.type)}
                // actions={notification.actions}
                actionsposition="bottom"
                fit="large"
                verticalalign="middle"
                appearance={mapTypeToAppearance(notification.type)}
              >
                <div className={styles.notificationBody}>{notification.message}</div>
                {/* Add icon slot */}
                {icon && (
                  <div slot="icon" className={styles.notificationIcon}>
                    <McIcon icon={icon} color={mapTypeToColor(notification.type)} />
                  </div>
                )}
                {notification.actions?.length > 0 && (
                  <span slot="actions">
                    {notification.actions?.map((action: any, index: number) => {
                      return (
                        <McButton
                          key={index}
                          loading={downloading}
                          fit="small"
                          onClick={async () => {
                            setDownloading(true);
                            await handleDownload(action.attachmentId);
                            setDownloading(false);
                          }}
                        >
                          Download
                        </McButton>
                      );
                    })}
                  </span>
                )}
              </McNotification>
              <McButton
                className={styles.notificationClose}
                appearance="neutral"
                fit="small"
                hiddenlabel
                icon="times"
                label="Close notification"
                padding="none"
                variant="plain"
                click={() => removeNotification(notification.id)}
              />
            </div>
          );
        })}
      </div>,
      document.body
    );
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        addNotification,
        removeNotification,
      }}
    >
      {children}
      {renderNotifications()}
    </NotificationContext.Provider>
  );
};

// Hook to use notification context
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error("useNotification must be used within a NotificationProvider");
  }
  return context;
};

// Helper functions
function mapTypeToAppearance(type: NotificationType): any {
  switch (type) {
    case "success":
      return "success";
    case "warning":
      return "warning";
    case "error":
      return "error";
    case "info":
      return "info";
    default:
      return "info";
  }
}
function mapTypeToColor(type: NotificationType): any {
  switch (type) {
    case "success":
      return "var(--mds_core_notification_success_icon_fill)";
    case "warning":
      return "var(--mds_core_notification_warning_icon_fill)";
    case "error":
      return "var(--mds_core_notification_error_icon_fill)";
    case "info":
      return "var(--mds_core_notification_info_icon_fill)";
    default:
      return "var(--mds_core_notification_info_icon_fill)";
  }
}

function getNotificationIcon(notification: NotificationItem): string {
  if (
    notification.type === "error" &&
    (!notification.icon ||
      notification.icon === "times-circle" ||
      notification.icon === "times-circle-solid")
  ) {
    return "exclamation-circle";
  }

  if (notification.icon) {
    return notification.icon;
  }

  switch (notification.type) {
    case "success":
      return "check-circle";
    case "warning":
      return "exclamation-triangle";
    case "error":
      return "exclamation-circle";
    case "info":
      return "info-circle";
    default:
      return "info-circle";
  }
}

function getDefaultTitle(type: NotificationType): string {
  switch (type) {
    case "success":
      return "Success";
    case "warning":
      return "Warning";
    case "error":
      return "Error";
    case "info":
      return "Information";
    default:
      return "Notification";
  }
}
